import { Assets, Rectangle, Texture } from 'pixi.js';
import type { BakedLightManifest } from './bakedManifest';
import lightManifestUrl from '../../assets/baked/lights/lights.json?url';
import lightAtlasUrl from '../../assets/baked/lights/lights.webp?url';

export interface LightTexture {
  texture: Texture;
  colored: boolean;
}

export type LightTextures = Map<string, LightTexture>;

function lightTextureKey(profile: number, shader?: number): string {
  return `${profile}:${shader ?? 'base'}`;
}

export function resolveLightTexture(
  textures: LightTextures,
  profile: number,
  shader?: number,
): LightTexture | undefined {
  return (
    (shader === undefined ? undefined : textures.get(lightTextureKey(profile, shader))) ??
    textures.get(lightTextureKey(profile))
  );
}

/** Load the exact ClassicUO light.mul profile masks baked by the offline pipeline. */
export async function loadLightTextures(): Promise<LightTextures> {
  // Fetched rather than inlined so the manifest stays off the cold entry
  // budget (ADR-0310); loadOptionalLightTextures already degrades on failure.
  const [manifest, atlas] = await Promise.all([
    fetch(lightManifestUrl).then((response) => {
      if (!response.ok) throw new Error(`light manifest ${response.status}: ${lightManifestUrl}`);
      return response.json() as Promise<BakedLightManifest>;
    }),
    Assets.load<Texture>(lightAtlasUrl),
  ]);
  atlas.source.scaleMode = 'linear';
  const textures: LightTextures = new Map();
  for (const profile of manifest.profiles) {
    textures.set(lightTextureKey(profile.profile, profile.shader), {
      texture: new Texture({
        source: atlas.source,
        frame: new Rectangle(profile.x, profile.y, profile.width, profile.height),
      }),
      colored: profile.shader !== undefined,
    });
  }
  return textures;
}

/** Optional presentation art must never block login or an otherwise valid scene. */
export async function loadOptionalLightTextures(
  load: () => Promise<LightTextures> = loadLightTextures,
  warn: (message: string) => void = (message) => console.warn(message),
): Promise<LightTextures> {
  try {
    return await load();
  } catch {
    warn('ClassicUO light atlas unavailable - static lights disabled');
    return new Map();
  }
}
